/* eslint-disable react-hooks/exhaustive-deps */
import useBrand from 'Hooks/useBrand'
import {
  useState,
  useCallback,
  createContext,
  ReactNode,
  useEffect,
} from "react"
import { CurrencyCodesType, SpendBudgetData } from "modules/Types"
import debounce from "lodash/debounce"
import { getCurrency, getCampaignSpendUsageData } from "./api"
import { AdPlatform, CampaignType, COMMON_WIZARD_STEPS, DEFAULT_CURRENCY, PLAYBOOK_TYPE_INFO, PlaybookType } from "modules/Const"
import useOrganization from 'Hooks/useOrganization'

const { FINALIZATION, BUDGET } = COMMON_WIZARD_STEPS

interface Context {
  data: SpendBudgetData | null
  loading: boolean
  currency: CurrencyCodesType
  onChangeCurrency: (currency: CurrencyCodesType) => void
  onChangeBudget: (budget: number) => void
}

export const SpendBudgetContext = createContext<Context>({
  data: null,
  loading: false,
  onChangeCurrency: () => undefined,
  onChangeBudget: () => undefined,
  currency: 'USD'
})

interface Props {
  fbAccountId?: string | null
  budgetValue: number
  step: string
  campaignType: CampaignType | PlaybookType
  children: ReactNode[] | ReactNode,
  access_token?: string
}

const SpendBudgetProvider = ({
  children,
  step,
  budgetValue,
  fbAccountId,
  campaignType,
  access_token
}: Props) => {
  const { currentBrand } = useBrand()
  const { organization, brandIdIsInOrganization, getOrganizationId } = useOrganization()
  const organizationId = getOrganizationId();

  // Get the brand slug from the current brand
  const brandSlug = currentBrand?.slug

  const inOrganization = brandIdIsInOrganization(currentBrand?.id)

  // Check if the current brand belongs to the user's organization
  // Returns false if there is no current brand, otherwise checks organization membership

  // Determine if we're in an organization context
  // Organization must be ready and brand must belong to the organization
  const isOrganization = Boolean(organization?.status === "ready" && inOrganization)

  const [data, setData] = useState<SpendBudgetData | null>(null)
  const [currency, setCurrency] = useState(currentBrand?.currency?.code || DEFAULT_CURRENCY)
  const [isInitialLoad, setIsInitialLoad] = useState(true)
  const [loading, setLoading] = useState(false)

  const onChangeBudget = useCallback(
    debounce(async (budget: number) => {
      setLoading(true)
      const data = await getCampaignSpendUsageData({ isOrganization, brandSlug, budget, currency, organizationId })
      setData(data)
      setLoading(false)
    }, 500),
    [currency]
  ) as unknown as (budget: number) => void

  const onChangeCurrency = async (currency: CurrencyCodesType) => {
    setCurrency(currency)
    if (budgetValue <= 0) return
    setLoading(true)
    const data = await getCampaignSpendUsageData({ isOrganization, brandSlug, budget: budgetValue, currency, organizationId })
    setData(data)
    setLoading(false)
  }

  useEffect(() => {
    if (isInitialLoad) {
      const initialize = async () => {
        let newCurrency = currency
        const isFBCampaign = PLAYBOOK_TYPE_INFO[campaignType]?.adPlatform === AdPlatform.META
        const validSteps = step === FINALIZATION || step === BUDGET

        if (validSteps && isFBCampaign) {
          newCurrency = await getCurrency(fbAccountId, access_token)
          setCurrency(newCurrency)
        }

        const data = await getCampaignSpendUsageData({
          brandSlug,
          isOrganization,
          budget: budgetValue,
          currency: newCurrency,
          organizationId
        })
        setData(data)
        setIsInitialLoad(false)
      }

      initialize()
    }
  }, [isInitialLoad, budgetValue, fbAccountId, campaignType, step])

  const context = {
    data,
    loading,
    currency,
    onChangeCurrency,
    onChangeBudget,
  }

  return (
    <SpendBudgetContext.Provider value={context}>
      {children}
    </SpendBudgetContext.Provider>
  )
}

export default SpendBudgetProvider
